import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { projectService } from '@/services'
import { Project, User } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import {
  PageContainer,
  PageHeader,
  ListPageLayout,
  LoadingState,
  MembersList,
} from '@/components/layout'

export default function ProjectMembersPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [project, setProject] = useState<Project | null>(null)
  const [members, setMembers] = useState<User[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [removingUserId, setRemovingUserId] = useState<string | null>(null)

  const loadProjectMembers = useCallback(async () => {
    if (!projectId) return

    try {
      const data = await projectService.getById(projectId)
      if (data) {
        setProject(data)
        setMembers(data.users)
      } else {
        toast.error('Project not found')
        navigate('/projects')
      }
    } catch {
      toast.error('Failed to load project members')
    } finally {
      setIsLoading(false)
    }
  }, [projectId, navigate])

  useEffect(() => {
    loadProjectMembers()
  }, [loadProjectMembers])

  const handleRemoveMember = async (userId: string) => {
    if (!project || !currentUser) return

    // Check if user is the creator
    if (project.creator.id !== currentUser.id) {
      toast.error('Only the project creator can remove members')
      return
    }

    if (userId === project.creator.id) {
      toast.error('Cannot remove the project creator')
      return
    }

    if (!confirm('Are you sure you want to remove this member from the project?')) {
      return
    }

    setRemovingUserId(userId)
    try {
      await projectService.removeUser(project.id, userId)
      toast.success('Member removed successfully')
      await loadProjectMembers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member')
    } finally {
      setRemovingUserId(null)
    }
  }

  const isCreator = currentUser && project && project.creator.id === currentUser.id

  if (isLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading members..." />
      </PageContainer>
    )
  }

  if (!project) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="Project Members"
        description={`${members.length} ${members.length === 1 ? 'member' : 'members'} in ${project.name}`}
        customActions={
          isCreator && (
            <Button onClick={() => navigate(`/projects/${project.id}/invite`)} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Members
            </Button>
          )
        }
      />

      <ListPageLayout
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search members..."
      >
        <MembersList
          members={members}
          searchQuery={searchQuery}
          creatorId={project.creator.id}
          currentUserId={currentUser?.id || ''}
          isCreator={!!isCreator}
          removingUserId={removingUserId}
          onRemoveMember={handleRemoveMember}
          emptyStateMessage="No members yet"
        />
      </ListPageLayout>
    </PageContainer>
  )
}
